<script setup lang="ts">
    import {
        AlertDialog,
        AlertDialogAction,
        AlertDialogCancel,
        AlertDialogContent,
        AlertDialogDescription,
        AlertDialogFooter,
        AlertDialogHeader,
        AlertDialogTitle,
    } from '@/components/ui/alert-dialog';
    import { ref, watch, computed } from 'vue';
    import { toast } from 'vue-sonner';
    import { useI18n } from 'vue-i18n';
    import { useMutation } from '@tanstack/vue-query';
    import { usePage } from '@inertiajs/vue3';
    import { useQueryClient } from '@tanstack/vue-query';
    import { useVoucherStore } from '@/stores/voucher';
    import axios from 'axios';
    import QueryKey from '@/enums/QueryKey';
    import type { SharedData, Voucher } from '@/types';
    import type { Table } from '@tanstack/vue-table';

    const { t } = useI18n();

    const voucherStore = useVoucherStore();
    const queryClient = useQueryClient();

    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth?.user.selected_company_id);

    const props = defineProps<{
        table: Table<Voucher>;
    }>();

    const validVoucherIds = ref<number[]>([]);

    watch(
        () => voucherStore.validVoucherIds,
        (newIds) => {
            validVoucherIds.value = newIds;
        },
        { immediate: true },
    );

    const { mutate, isPending } = useMutation({
        mutationFn: (formData: { ids: number[] }) =>
            axios.post(route('v2.voucher.upload.selection'), formData),
        onSuccess: (data) => {
            if (data.data?.success) {
                toast.success(data.data.success);
            }
            if (data.data?.errorMessages?.length) {
                data.data.errorMessages.forEach((error: string) => {
                    toast.error(error);
                });
            }
            props.table.resetRowSelection();
            queryClient.invalidateQueries({
                queryKey: [QueryKey.VOUCHERS, selectedCompanyId.value],
            });
        },
        onError: (error: any) => {
            toast.error(error.response.data.message || t('forms.system_error'));
        },
    });

    function approve() {
        mutate({
            ids: validVoucherIds.value,
        });
    }
</script>

<template>
    <AlertDialog
        :open="voucherStore.approveAlertOpen"
        @update:open="(value) => voucherStore.toggleOpen('approve', value)"
    >
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>{{ t('vouchers.select.upload.title') }}</AlertDialogTitle>
                <AlertDialogDescription class="flex flex-col gap-2">
                    <i18n-t
                        keypath="vouchers.select.upload.content"
                        tag="span"
                        scope="global"
                    >
                        <template #body>
                            <span class="font-bold">
                                {{ voucherStore.validVoucherNumbers }}
                            </span>
                        </template>
                    </i18n-t>

                    {{ t('vouchers.select.upload.description') }}
                </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel>{{ t('forms.no') }}</AlertDialogCancel>
                <AlertDialogAction
                    variant="default"
                    @click="approve"
                >
                    {{ t('forms.yes') }}
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
</template>
